import { useEffect, useMemo, useState } from 'react';
import {
  CalendarMonth,
  CheckCircleOutline,
  NotificationsActive,
  OpenInNew,
  Sync,
} from '@mui/icons-material';
import {
  Alert as MuiAlert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { createFileRoute } from '@tanstack/react-router';
import { ProductRouteGuard } from '@/components/ProductRouteGuard';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import * as dueDatesApi from '@/api/dueDates';
import { listCuitProfiles } from '@/api/cuitProfiles';
import { toApiError } from '@/api/client';
import { useCurrentCuit } from '@/auth/CuitContext';
import { EmptyState } from '@/components/EmptyState';
import { formatDateTime } from '@/lib/formatters';
import { safeHttpsUrl } from '@/lib/safeExternalUrl';

export const Route = createFileRoute('/_authenticated/vencimientos/')({ component: DueDatesRoute });

function formatDateOnly(value: string): string {
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('DD/MM/YYYY') : value;
}


function DueDatesRoute(): JSX.Element {
  return (
    <ProductRouteGuard
      products={['Estudio', 'Empresa']}
      requirePermission="canViewInvoices"
      title="Vencimientos no disponibles"
      description="Seleccioná un producto fiscal con permisos para ver comprobantes antes de consultar vencimientos."
    >
      <DueDatesPage />
    </ProductRouteGuard>
  );
}

function DueDatesPage(): JSX.Element {
  const queryClient = useQueryClient();
  const { currentCuitProfileId, setCurrentCuitProfile } = useCurrentCuit();
  const [selectedTaxes, setSelectedTaxes] = useState<string[]>([]);
  const [taxFilter, setTaxFilter] = useState('');
  const [toast, setToast] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);
  const from = dayjs().format('YYYY-MM-DD');
  const to = dayjs().add(120, 'day').format('YYYY-MM-DD');

  const profilesQ = useQuery({
    queryKey: ['cuit-profiles', 'due-dates'],
    queryFn: () => listCuitProfiles(0, 200),
  });
  const statusQ = useQuery({
    queryKey: ['due-dates', 'status'],
    queryFn: dueDatesApi.getDueDateStatus,
    refetchInterval: 5 * 60_000,
  });
  const subscriptionsQ = useQuery({
    queryKey: ['due-dates', 'subscriptions', currentCuitProfileId],
    queryFn: () => dueDatesApi.listDueDateSubscriptions(currentCuitProfileId!),
    enabled: Boolean(currentCuitProfileId),
  });
  const dueDatesQ = useQuery({
    queryKey: ['due-dates', currentCuitProfileId, from, to, taxFilter],
    queryFn: () => dueDatesApi.listDueDates({
      cuitProfileId: currentCuitProfileId!,
      from,
      to,
      taxName: taxFilter || undefined,
      take: 500,
    }),
    enabled: Boolean(currentCuitProfileId),
  });

  useEffect(() => {
    setSelectedTaxes((subscriptionsQ.data ?? []).filter((item) => item.subscribed).map((item) => item.taxName));
  }, [subscriptionsQ.data]);

  const options = subscriptionsQ.data ?? [];
  const taxNames = useMemo(() => options.map((item) => item.taxName), [options]);
  const suggestedTaxes = useMemo(
    () => options.filter((item) => item.suggested).map((item) => item.taxName),
    [options],
  );
  const selectedProfile = profilesQ.data?.items.find((profile) => profile.id === currentCuitProfileId);

  const saveMut = useMutation({
    mutationFn: () => dueDatesApi.replaceDueDateSubscriptions(currentCuitProfileId!, selectedTaxes),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['due-dates', 'subscriptions', currentCuitProfileId] }),
        queryClient.invalidateQueries({ queryKey: ['due-dates', currentCuitProfileId] }),
      ]);
      setToast({ message: 'Seguimiento de vencimientos actualizado.', severity: 'success' });
    },
    onError: (error) => setToast({ message: toApiError(error).message, severity: 'error' }),
  });

  const sync = statusQ.data?.lastSync ?? dueDatesQ.data?.lastSync ?? null;
  const syncFailed = sync?.status === 'Failed';
  const officialSourceUrl = safeHttpsUrl(statusQ.data?.sourceUrl) ?? 'https://seti.afip.gob.ar/av/seleccionVencimientos.do';

  return (
    <Box>
      <Stack spacing={3}>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" gap={2}>
          <Stack direction="row" spacing={2} alignItems="center">
            <CalendarMonth color="primary" sx={{ fontSize: 38 }} />
            <Box>
              <Typography variant="h4">Vencimientos ARCA</Typography>
              <Typography variant="body2" color="text.secondary">
                Calendario oficial actualizado diariamente y resuelto por terminación de CUIT.
              </Typography>
            </Box>
          </Stack>
          <Button
            component="a"
            href={officialSourceUrl}
            target="_blank"
            rel="noreferrer"
            variant="outlined"
            endIcon={<OpenInNew />}
            sx={{ alignSelf: { xs: 'stretch', md: 'center' } }}
          >
            Ver fuente oficial
          </Button>
        </Stack>

        {syncFailed ? (
          <MuiAlert severity="warning">
            La última actualización falló. Tilde conserva el último calendario válido y volverá a intentar automáticamente.
            {sync?.error ? ` Detalle: ${sync.error}` : ''}
          </MuiAlert>
        ) : sync ? (
          <MuiAlert severity="success" icon={<Sync />}>
            Última actualización correcta: {formatDateTime(sync.completedAt ?? sync.startedAt)} · {sync.fetched} vencimientos procesados.
          </MuiAlert>
        ) : (
          <MuiAlert severity="info">El calendario todavía no fue sincronizado. El proceso se ejecuta al iniciar el servicio y luego cada 24 horas.</MuiAlert>
        )}

        <Paper variant="outlined" sx={{ p: 3 }}>
          <Stack spacing={2}>
            <Box>
              <Typography variant="h6">CUIT a consultar</Typography>
              <Typography variant="body2" color="text.secondary">
                La fecha exacta se determina con el último dígito del CUIT. Los vencimientos publicados para “Todos” también se incluyen.
              </Typography>
            </Box>
            <FormControl fullWidth sx={{ maxWidth: 560 }}>
              <InputLabel id="due-date-cuit-label">Contribuyente</InputLabel>
              <Select
                labelId="due-date-cuit-label"
                label="Contribuyente"
                value={currentCuitProfileId ?? ''}
                onChange={(event) => setCurrentCuitProfile(event.target.value || null)}
                disabled={profilesQ.isLoading}
              >
                <MenuItem value=""><em>Seleccionar un CUIT</em></MenuItem>
                {(profilesQ.data?.items ?? []).map((profile) => (
                  <MenuItem key={profile.id} value={profile.id}>
                    {profile.razonSocial} · {profile.cuit}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </Paper>

        {!currentCuitProfileId ? (
          <EmptyState
            icon={<CalendarMonth fontSize="inherit" />}
            title="Seleccioná un contribuyente"
            description="Así Tilde puede mostrar solo la fecha que corresponde a la terminación de su CUIT."
          />
        ) : (
          <>
            <Paper variant="outlined" sx={{ p: 3 }}>
              <Stack spacing={2}>
                <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" gap={2}>
                  <Box>
                    <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <NotificationsActive color="primary" /> Seguimiento y avisos
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Elegí los impuestos de {selectedProfile?.razonSocial ?? 'este CUIT'}. Tilde crea alertas y envía avisos 7, 3 y 1 día antes.
                    </Typography>
                  </Box>
                  {suggestedTaxes.length > 0 && (
                    <Button size="small" onClick={() => setSelectedTaxes(Array.from(new Set([...selectedTaxes, ...suggestedTaxes])))}>
                      Agregar sugeridos
                    </Button>
                  )}
                </Stack>
                <Autocomplete
                  multiple
                  disableCloseOnSelect
                  options={taxNames}
                  value={selectedTaxes}
                  loading={subscriptionsQ.isLoading}
                  onChange={(_, values) => setSelectedTaxes(values)}
                  renderOption={(props, option, state) => (
                    <li {...props}>
                      <Checkbox checked={state.selected} sx={{ mr: 1 }} />
                      <ListItemText
                        primary={option}
                        secondary={suggestedTaxes.includes(option) ? 'Sugerido según condición fiscal' : undefined}
                      />
                    </li>
                  )}
                  renderInput={(params) => <TextField {...params} label="Impuestos a seguir" placeholder="Buscar impuesto" />}
                />
                <Button
                  variant="contained"
                  startIcon={saveMut.isPending ? <CircularProgress size={18} color="inherit" /> : <CheckCircleOutline />}
                  disabled={saveMut.isPending || subscriptionsQ.isLoading}
                  onClick={() => saveMut.mutate()}
                  sx={{ alignSelf: 'flex-start' }}
                >
                  Guardar seguimiento
                </Button>
              </Stack>
            </Paper>

            <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
              <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" gap={2} sx={{ p: 3 }}>
                <Box>
                  <Typography variant="h6">Próximos 120 días</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Fechas aplicables al CUIT {selectedProfile?.cuit ?? ''}. ARCA puede publicar distintas fechas para una misma obligación.
                  </Typography>
                </Box>
                <FormControl size="small" sx={{ minWidth: 260 }}>
                  <InputLabel id="tax-filter-label">Impuesto</InputLabel>
                  <Select labelId="tax-filter-label" label="Impuesto" value={taxFilter} onChange={(event) => setTaxFilter(event.target.value)}>
                    <MenuItem value="">Todos</MenuItem>
                    {taxNames.map((tax) => <MenuItem key={tax} value={tax}>{tax}</MenuItem>)}
                  </Select>
                </FormControl>
              </Stack>
              {dueDatesQ.isLoading ? (
                <Stack alignItems="center" sx={{ py: 8 }}><CircularProgress /></Stack>
              ) : dueDatesQ.error ? (
                <MuiAlert severity="error" sx={{ m: 3 }}>{toApiError(dueDatesQ.error).message}</MuiAlert>
              ) : (dueDatesQ.data?.items.length ?? 0) === 0 ? (
                <Box sx={{ p: 3 }}>
                  <EmptyState
                    icon={<CalendarMonth fontSize="inherit" />}
                    title="Sin vencimientos en el período"
                    description="No hay fechas oficiales aplicables a este CUIT en los próximos 120 días."
                  />
                </Box>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Vence</TableCell>
                        <TableCell>Impuesto</TableCell>
                        <TableCell>Obligación</TableCell>
                        <TableCell>Período</TableCell>
                        <TableCell>Terminación CUIT</TableCell>
                        <TableCell>Seguimiento</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(dueDatesQ.data?.items ?? []).map((item) => (
                        <TableRow key={item.id} hover>
                          <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 700 }}>{formatDateOnly(item.dueDate)}</TableCell>
                          <TableCell>{item.taxName}</TableCell>
                          <TableCell>{item.obligation}</TableCell>
                          <TableCell>{item.period ?? '—'}</TableCell>
                          <TableCell><Chip size="small" label={item.cuitTermination} variant="outlined" /></TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              color={item.isSubscribed ? 'success' : 'default'}
                              label={item.isSubscribed ? 'Activo' : 'Sin avisos'}
                              variant={item.isSubscribed ? 'filled' : 'outlined'}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          </>
        )}
      </Stack>

      <Snackbar open={Boolean(toast)} autoHideDuration={4000} onClose={() => setToast(null)}>
        <MuiAlert severity={toast?.severity ?? 'info'} variant="filled" onClose={() => setToast(null)}>
          {toast?.message}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
}
